import {isEnabled} from '~/src/featureFlags'
import type {Notification} from '~/src/models/notification'
import {
  INVITEE_PENDING_ORDERS_PATH,
  invitationNotificationSide,
  isESignNotification,
  isEVerifyNotification,
  isJobOrderInvitationNotification,
  isTimeOffNotification,
} from '~/src/models/notification'
import {SIDEBAR_ROUTES} from '~/src/routes/constants'

export interface NotificationDestination {
  path: string
  state?: {notificationId?: string; requestId?: string}
  /**
   * Select this branch before opening `path` (useOpenNotificationDestination). Only an invitee's job
   * order invitation sets it (STFFLG-4184), when it was sent to another of their branches than the
   * one selected in the header: Pending Orders lists only the selected branch's invitations. The
   * page then opens only if that branch's role opens it.
   */
  agencyId?: string
}

export interface NotificationDestinationOptions {
  /** The layout the user is in; a candidate's completed eSign opens their own documents. */
  variant?: string
  /** settings > work_authorization View: whether the Work Authorization page is reachable. */
  canViewWorkAuthorization: boolean
  /**
   * Whether Manage Orders › Pending Orders opens for this role (manage_orders > service_orders,
   * the route guard's own answer: useInvitationNotificationAccess). Absent = no.
   */
  canOpenPendingOrders?: boolean
  /** Whether a job order's page opens for this role (manage_orders > job_orders). Absent = no. */
  canOpenJobOrders?: boolean
  /** The branch selected in the header (`loggedInAgencyId`). */
  loggedInAgencyId?: string | null
  /**
   * The branches the header's switcher offers (useSwitchableAgencies). An invitee's invitation sent
   * to one of these other than the selected branch opens Pending Orders in that branch. Absent =
   * none, so the action reads the selected branch, as before.
   */
  switchableAgencyIds?: readonly string[]
}

/**
 * Where opening a notification takes the user. Shared by the bell and the inbox so the two
 * never disagree. Anything without a page of its own lands on the inbox, which every role
 * that sees the bell can open (the Work Authorization page needs a Settings permission).
 *
 * Null means the notification has no action at all, not even the inbox: the card and the inbox
 * row show none (an invitation that was withdrawn, or one whose page this role cannot open).
 */
export function getNotificationDestination(
  notification: Notification,
  {
    variant,
    canViewWorkAuthorization,
    canOpenPendingOrders = false,
    canOpenJobOrders = false,
    loggedInAgencyId,
    switchableAgencyIds = [],
  }: NotificationDestinationOptions,
): NotificationDestination | null {
  const inbox: NotificationDestination = {
    path: SIDEBAR_ROUTES.NOTIFICATIONS,
    state: {notificationId: notification.id},
  }

  // TODO(feature-flag): orderAccessInvites — remove the isEnabled check when the flag is deleted
  // (flag off: the types are never sent, and one that arrives anyway falls through to the inbox).
  if (isEnabled('orderAccessInvites') && isJobOrderInvitationNotification(notification.type)) {
    return getInvitationDestination(notification, {
      canOpenPendingOrders,
      canOpenJobOrders,
      loggedInAgencyId,
      switchableAgencyIds,
    })
  }

  // TODO(feature-flag): candidateTimeOff — remove when flag is deleted
  if (isEnabled('candidateTimeOff') && isTimeOffNotification(notification.type)) {
    return {path: '/my-profile?tab=time-off'}
  }

  if (isESignNotification(notification.type)) {
    const requestId = notification.metadata?.requestId
    if (!requestId) return inbox

    if (notification.type === 'ESIGN_SIGNING_REQUEST' || notification.type === 'ESIGN_REMINDER') {
      return {path: `/signing/${requestId}`}
    }
    if (variant === 'candidate') return {path: '/documents?tab=signDocs'}
    // '/documents/signatures' was left behind when Signatures moved under Settings — it is
    // not a route in routeConfig at all, so an agency user clicking a completion
    // notification landed nowhere. The management page is the right destination here (this
    // branch is the SENDER being told their document finished, and they reached the send
    // flow through Settings in the first place, so the permission gate is satisfied).
    return {path: '/settings/documents/signatures', state: {requestId}}
  }

  if (isEVerifyNotification(notification.type) && canViewWorkAuthorization) {
    return {path: SIDEBAR_ROUTES.SETTINGS_E_VERIFY, state: {notificationId: notification.id}}
  }

  return inbox
}

/**
 * A job order invitation's action (STFFLG-4184), built from its metadata rather than `ctaLink`:
 * - the invitee's RECEIVED and EXPIRED: View Invitation / View Pending Orders → their Pending
 *   Orders (an expired card has left it, which is the answer). Pending Orders lists the selected
 *   branch's invitations only, so one sent to another of the invitee's branches selects that branch
 *   first (`agencyId`, invitationBranchToSelect); whether that branch's role opens the page is
 *   answered once it is selected, so the action is offered whatever the current branch's answer;
 * - the host side's ACCEPTED, DECLINED and EXPIRED: Open Order → the ROOT order's page;
 * - WITHDRAWN: none. The invitation or the helper's access was taken back, so the order is no
 *   longer theirs to open, and the message already says why.
 * A role that cannot open the page gets no action rather than a page that answers 401
 * (permission-aware fallback), as does a host-side notification with no order id.
 */
function getInvitationDestination(
  notification: Notification,
  {
    canOpenPendingOrders,
    canOpenJobOrders,
    loggedInAgencyId,
    switchableAgencyIds,
  }: {
    canOpenPendingOrders: boolean
    canOpenJobOrders: boolean
    loggedInAgencyId: string | null | undefined
    switchableAgencyIds: readonly string[]
  },
): NotificationDestination | null {
  if (notification.type === 'JOB_ORDER_INVITE_WITHDRAWN') return null
  if (invitationNotificationSide(notification) === 'invitee') {
    const agencyId = invitationBranchToSelect(notification, loggedInAgencyId, switchableAgencyIds)
    if (agencyId) return {path: INVITEE_PENDING_ORDERS_PATH, agencyId}
    return canOpenPendingOrders ? {path: INVITEE_PENDING_ORDERS_PATH} : null
  }
  const jobOrderId = notification.metadata?.jobOrderId
  if (!jobOrderId || !canOpenJobOrders) return null
  return {path: `${SIDEBAR_ROUTES.JOB_ORDER_DETAILS_SUMMARY}/${encodeURIComponent(jobOrderId)}`}
}

const sameAgencyId = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * The branch an invitee's invitation notification must select before Pending Orders shows it, or
 * null when none is needed or possible. mspplatform writes the invitee's notification under the
 * invitation's `inviteeAgencyId` (their home branch), and `/invitations/mine` lists only the selected
 * branch's invitations, so a multi-branch invitee working in another branch (the sign-in pages
 * select `agencyAssignments[0]`) would land on a page without the card. Null when no branch is
 * selected (the API then reads the token's), when it is already selected, or when the switcher does
 * not offer it. Ids compare case-insensitively; the switcher's spelling is returned.
 */
export function invitationBranchToSelect(
  notification: Pick<Notification, 'agencyId'>,
  loggedInAgencyId: string | null | undefined,
  switchableAgencyIds: readonly string[],
): string | null {
  const target = notification.agencyId?.trim()
  const selected = loggedInAgencyId?.trim()
  if (!target || !selected || sameAgencyId(target, selected)) return null
  return switchableAgencyIds.find((id) => !!id && sameAgencyId(id, target)) ?? null
}
